import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";
import { Currency } from "@/core/services/api/interfaces/dict/Currency";
import { PortalService } from "@/core/services/api/interfaces/dict/PortalService";
import {
  CryptoNetwork,
  CryptoNetworks,
} from "@/core/services/api/interfaces/dict/CryptoNetwork";
import {
  EnumValue,
  Feature,
  OnboardingStage,
} from "@/core/services/api/enums/Common";

export interface CountryItem {
  id: string;
  flag: string;
  text: string;
}

@Module
export default class DictionaryModule extends VuexModule {
  countries = [] as [] | CountryItem[];
  company_features = [] as [] | Feature[];
  fiat_currencies = [] as [] | Currency[];
  crypto_currencies = [] as [] | Currency[];
  portal_services = [] as [] | PortalService[];
  crypto_networks = {} as CryptoNetworks;
  company_industries = [] as EnumValue[];
  onboarding_stages = [] as OnboardingStage[];
  employee_business_roles = [] as EnumValue[];
  user_document_types = [] as EnumValue[];
  user_roles = [] as EnumValue[];
  payment_service_statuses = [] as EnumValue[];
  company_corporation_types = [] as EnumValue[];
  company_payment_services = [] as EnumValue[];

  get getCountries(): [] | CountryItem[] {
    return this.countries;
  }

  get getEmployeeBusinessRoles(): EnumValue[] {
    return this.employee_business_roles;
  }

  get getUserRoles(): EnumValue[] {
    return this.user_roles;
  }

  get getUserDocumentTypes(): EnumValue[] {
    return this.user_document_types;
  }

  get getCryptoNetworks(): CryptoNetworks {
    return this.crypto_networks;
  }

  get getCompanyIndustries(): EnumValue[] {
    return this.company_industries;
  }

  get getCompanyCorporationTypes(): EnumValue[] {
    return this.company_corporation_types;
  }

  get getCompanyPaymentServices(): EnumValue[] {
    return this.company_payment_services;
  }

  get getCompanyPaymentServicesIndexed(): { [index: string]: EnumValue } {
    return this.company_payment_services.reduce((acc, service) => {
      acc[service.value] = service;
      return acc;
    }, {});
  }

  get getCompanyPaymentServiceStatusesIndexed(): {
    [index: string]: EnumValue;
  } {
    return this.payment_service_statuses.reduce((acc, service) => {
      acc[service.value] = service;
      return acc;
    }, {});
  }

  get getOnboardingStagesIndexed(): {
    [index: string]: OnboardingStage;
  } {
    return this.onboarding_stages.reduce((acc, service) => {
      acc[service.value] = service;
      return acc;
    }, {});
  }

  get getCompanyFeatures(): Feature[] {
    return this.company_features;
  }

  get getCryptoNetworksForCrypto() {
    return (crypto: string): CryptoNetwork[] =>
      this.crypto_networks[crypto] || [];
  }

  get getPortalServices(): PortalService[] {
    return this.portal_services;
  }

  get getPortalServiceFirst(): PortalService {
    return this.portal_services[0];
  }

  get getFiatCurrencies(): [] | Currency[] {
    return this.fiat_currencies;
  }

  get getDefaultFiatCurrencyCode(): string {
    return "EUR";
  }

  get getDefaultCryptoCurrencyCode(): string {
    return "BTC";
  }

  get getCryptoCurrencies(): [] | Currency[] {
    return this.crypto_currencies;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_COUNTRIES](countries: CountryItem[] | []) {
    this.countries = countries;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_COMPANY_FEATURES](features: Feature[] | []) {
    this.company_features = features;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_COMPANY_INDUSTRIES](
    industries: EnumValue[] | []
  ) {
    this.company_industries = industries;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_ONBOARDING_STAGES](
    onboarding_stages: OnboardingStage[] | []
  ) {
    this.onboarding_stages = onboarding_stages;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_EMPLOYEE_BUSINESS_ROLES](
    roles: EnumValue[] | []
  ) {
    this.employee_business_roles = roles;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_USER_DOCUMENT_TYPES](
    doc_types: EnumValue[] | []
  ) {
    this.user_document_types = doc_types;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_USER_ROLES](roles: EnumValue[] | []) {
    this.user_roles = roles;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_PAYMENT_SERVICE_STATUSES](
    statuses: EnumValue[] | []
  ) {
    this.payment_service_statuses = statuses;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_CORPORATION_TYPES](types: EnumValue[] | []) {
    this.company_corporation_types = types;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_COMPANY_PAYMENT_SERVICES](
    services: EnumValue[] | []
  ) {
    this.company_payment_services = services;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_PORTAL_SERVICES](
    services: PortalService[] | []
  ) {
    this.portal_services = services;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_FIAT_CURRENCIES](
    currencies: Currency[] | []
  ) {
    this.fiat_currencies = currencies;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_CRYPTO_CURRENCIES](
    currencies: Currency[] | []
  ) {
    this.crypto_currencies = currencies;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_SET_CRYPTO_NETWORKS](networks) {
    this.crypto_networks = networks as CryptoNetworks;
  }

  @Mutation
  [Mutations.COMMON_DICTIONARY_PURGE]() {
    this.countries = [] as [] | CountryItem[];
    this.fiat_currencies = [] as [] | Currency[];
    this.crypto_currencies = [] as [] | Currency[];
    this.portal_services = [] as [] | PortalService[];
    this.crypto_networks = {} as CryptoNetworks;
    this.company_features = [] as [] | Feature[];
    this.company_industries = [] as EnumValue[];
    this.company_corporation_types = [] as EnumValue[];
    this.company_payment_services = [] as EnumValue[];
  }

  @Action({ rawError: true })
  [Actions.COMMON_DICTIONARY_COUNTRIES_LOAD]() {
    return ApiService.common.dictionary
      .countries()
      .then(ApiService.unwrapItemResponse<{ [index: string]: string }>)
      .then((response) => {
        const countries = Object.entries(response).map(([id, name]) => {
          return {
            id: id,
            text: name,
            flag: `/media/flags/${id.toLowerCase()}.png`,
          } as CountryItem;
        });
        console.log(countries);
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_COUNTRIES,
          countries
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_COUNTRIES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  [Actions.GUEST_DICTIONARY_COUNTRIES_LOAD]() {
    return ApiService.common.dictionary
      .guestCountries()
      .then(ApiService.unwrapItemResponse<{ [index: string]: string }>)
      .then((response) => {
        const countries = Object.entries(response).map(([id, name]) => {
          return {
            id: id,
            text: name,
            flag: `/media/flags/${id.toLowerCase()}.png`,
          } as CountryItem;
        });
        console.log(countries);
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_COUNTRIES,
          countries
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_COUNTRIES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_DICTIONARY_CRYPTO_NETWORKS_LOAD]() {
    return ApiService.common.dictionary
      .cryptoNetworks()
      .then(ApiService.unwrapItemResponse)
      .then((response) => {
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_CRYPTO_NETWORKS,
          response
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_CRYPTO_NETWORKS_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_DICTIONARY_COMPANY_INDUSTRIES_LOAD]() {
    return ApiService.common.dictionary
      .companyIndustries()
      .then(ApiService.unwrapItemResponse)
      .then((response) => {
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_COMPANY_INDUSTRIES,
          response
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_COMPANY_INDUSTRIES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_DICTIONARY_ONBOARDING_STAGES_LOAD]() {
    return ApiService.common.dictionary
      .onboardingStages()
      .then(ApiService.unwrapItemsResponse<OnboardingStage[]>)
      .then((response) => {
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_ONBOARDING_STAGES,
          response
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_ONBOARDING_STAGES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_DICTIONARY_EMPLOYEE_BUSINESS_ROLES_LOAD]() {
    return ApiService.common.dictionary
      .employeeBusinessRoles()
      .then(ApiService.unwrapItemsResponse<EnumValue>)
      .then((response) => {
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_EMPLOYEE_BUSINESS_ROLES,
          response
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_EMPLOYEE_BUSINESS_ROLES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_DICTIONARY_USER_DOCUMENT_TYPES_LOAD]() {
    return ApiService.common.dictionary
      .userDocumentTypes()
      .then(ApiService.unwrapItemsResponse<EnumValue>)
      .then((response) => {
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_USER_DOCUMENT_TYPES,
          response
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_USER_DOCUMENT_TYPES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_DICTIONARY_USER_ROLES_LOAD]() {
    return ApiService.common.dictionary
      .userRoles()
      .then(ApiService.unwrapItemsResponse<EnumValue>)
      .then((response) => {
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_USER_ROLES,
          response
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_USER_ROLES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_DICTIONARY_COMPANY_PAYMENT_SERVICE_STATUSES_LOAD]() {
    return ApiService.common.dictionary
      .paymentServiceStatuses()
      .then(ApiService.unwrapItemsResponse<EnumValue[]>)
      .then((response) => {
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_PAYMENT_SERVICE_STATUSES,
          response
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_COMPANY_PAYMENT_SERVICE_STATUSES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_DICTIONARY_CORPORATION_TYPES_LOAD]() {
    return ApiService.common.dictionary
      .corporationTypes()
      .then(ApiService.unwrapItemResponse)
      .then((response) => {
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_CORPORATION_TYPES,
          response
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_CORPORATION_TYPES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_DICTIONARY_COMPANY_PAYMENT_SERVICES_LOAD]() {
    return ApiService.common.dictionary
      .companyPaymentServices()
      .then(ApiService.unwrapItemResponse)
      .then((response) => {
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_COMPANY_PAYMENT_SERVICES,
          response
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_COMPANY_PAYMENT_SERVICES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_DICTIONARY_COMPANY_FEATURES_LOAD]() {
    return ApiService.common.dictionary
      .companyFeatures()
      .then(ApiService.unwrapItemResponse<Feature>)
      .then((response) => {
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_COMPANY_FEATURES,
          response
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_COMPANY_FEATURES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  [Actions.COMMON_DICTIONARY_PORTAL_SERVICES_LOAD]() {
    return ApiService.common.dictionary
      .portalServices()
      .then(ApiService.unwrapItemResponse<PortalService[]>)
      .then((response) => {
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_PORTAL_SERVICES,
          response
        );
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_PORTAL_SERVICES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  [Actions.COMMON_DICTIONARY_CURRENCIES_LOAD]() {
    return Promise.all([
      this.context.dispatch(Actions.COMMON_DICTIONARY_FIAT_CURRENCIES_LOAD),
      this.context.dispatch(Actions.COMMON_DICTIONARY_CRYPTO_CURRENCIES_LOAD),
    ])
      .then(() => {
        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.COMMON_DICTIONARY_CURRENCIES_LOAD
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  [Actions.COMMON_DICTIONARY_FIAT_CURRENCIES_LOAD]() {
    return ApiService.common.dictionary
      .fiatCurrencies()
      .then(ApiService.unwrapItemResponse)
      .then((currencies) => {
        console.log(currencies);
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_FIAT_CURRENCIES,
          currencies
        );
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  [Actions.COMMON_DICTIONARY_CRYPTO_CURRENCIES_LOAD]() {
    return ApiService.common.dictionary
      .cryptoCurrencies()
      .then(ApiService.unwrapItemResponse)
      .then((currencies) => {
        console.log(currencies);
        this.context.commit(
          Mutations.COMMON_DICTIONARY_SET_CRYPTO_CURRENCIES,
          currencies
        );
      })
      .catch(console.error);
  }
}
